import type { Breakpoints } from '@/hooks/utils/useBreakpoints';
import { BREAKPOINT_CONSTANTS, BREAKPOINT_SIZES } from '@/utils/constants/ui';

import parseCssPx from './parseCssPx';

/**
 * Derives the same breakpoint flags as {@link useBreakpoints} from a viewport width in CSS pixels.
 * Used for SSR when the server has an approximate width (Client Hints or persisted cookie).
 *
 * @param sizes - Defaults to {@link BREAKPOINT_SIZES}; override in unit tests to avoid SCSS / load-order coupling.
 */
const getBreakpointInfoFromViewportWidth = (
    widthPx: number,
    sizes: typeof BREAKPOINT_SIZES = BREAKPOINT_SIZES,
): Breakpoints => {
    const w = widthPx;

    const mpMax = parseCssPx(sizes.MOBILE_PORTRAIT_MAX);
    const mlMin = parseCssPx(sizes.MOBILE_LANDSCAPE_MIN);
    const mlMax = parseCssPx(sizes.MOBILE_LANDSCAPE_MAX);
    const tpMin = parseCssPx(sizes.TABLET_PORTRAIT_MIN);
    const tpMax = parseCssPx(sizes.TABLET_PORTRAIT_MAX);
    const tlMin = parseCssPx(sizes.TABLET_LANDSCAPE_MIN);
    const tlMax = parseCssPx(sizes.TABLET_LANDSCAPE_MAX);
    const dMin = parseCssPx(sizes.DESKTOP_MIN);
    const dMax = parseCssPx(sizes.DESKTOP_MAX);
    const dwMin = parseCssPx(sizes.DESKTOP_WIDE_MIN);

    const isMobilePortrait = w <= mpMax;
    const isMobileLandscape = w >= mlMin && w <= mlMax;
    const isTabletPortrait = w >= tpMin && w <= tpMax;
    const isTabletLandscape = w >= tlMin && w <= tlMax;
    const isDesktop = w >= dMin && w <= dMax;
    const isDesktopWide = w >= dwMin;

    const isMobile = isMobileLandscape || isMobilePortrait || isTabletLandscape || isTabletPortrait;
    const isTabletPortraitOrWider = w >= tpMin;
    const isTabletLandscapeOrWider = w >= tlMin;

    let active: Breakpoints['active'] = BREAKPOINT_CONSTANTS.DESKTOP;
    if (isMobilePortrait) {
        active = BREAKPOINT_CONSTANTS.MOBILE_PORTRAIT;
    } else if (isMobileLandscape) {
        active = BREAKPOINT_CONSTANTS.MOBILE_LANDSCAPE;
    } else if (isTabletPortrait) {
        active = BREAKPOINT_CONSTANTS.TABLET_PORTRAIT;
    } else if (isTabletLandscape) {
        active = BREAKPOINT_CONSTANTS.TABLET_LANDSCAPE;
    } else if (isDesktop) {
        active = BREAKPOINT_CONSTANTS.DESKTOP;
    } else if (isDesktopWide) {
        active = BREAKPOINT_CONSTANTS.DESKTOP_WIDE;
    }

    return {
        isMobilePortrait,
        isMobileLandscape,
        isTabletPortrait,
        isTabletLandscape,
        isDesktop,
        isDesktopWide,
        isMobile,
        isTabletPortraitOrWider,
        isTabletLandscapeOrWider,
        active,
    };
};

export default getBreakpointInfoFromViewportWidth;
